/**
 * PM Training Clip 1 — Creating a Competition
 * Full browser, no zoom. Modal overlays same view. AutoCursor targets by ID.
 * 390 frames at 30fps = 13 seconds.
 *
 * Flow: Dashboard → click "NEW COMPETITION" → modal appears → type name →
 * enter description → select scoring model "Olympic" → set judge count to 5 → Create.
 */

import { AbsoluteFill, Sequence, useCurrentFrame, interpolate } from "remotion";
import { AutoCursor } from "../components/AutoCursor";
import { ScreenMock } from "../components/ScreenMock";
import { FadeTransition } from "../components/FadeTransition";
import { StepLabel } from "../components/StepLabel";

export function PMCourse1() {
  return (
    <AbsoluteFill style={{ backgroundColor: "#0a0a0b", fontFamily: "'Inter', sans-serif" }}>
      <Sequence durationInFrames={65}><Title /></Sequence>
      <Sequence from={55} durationInFrames={540}>
        <FadeTransition durationInFrames={540} fadeIn={10} fadeOut={10}><Demo /></FadeTransition>
      </Sequence>
      <Sequence from={590} durationInFrames={60}><Done /></Sequence>
    </AbsoluteFill>
  );
}

function Title() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12, 40, 55], [0, 1, 1, 0], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ fontSize: 10, color: "#a855f7", textTransform: "uppercase", letterSpacing: 3, fontFamily: "'JetBrains Mono', monospace", marginBottom: 14 }}>Competition Manager Training · Clip 1</div>
      <div style={{ fontSize: 52, fontWeight: 800, color: "#fff" }}>Creating a Competition</div>
    </AbsoluteFill>
  );
}

function Demo() {
  const frame = useCurrentFrame();
  const showModal = frame >= 70;
  const modalOp = showModal ? interpolate(frame, [70, 80], [0, 1], { extrapolateRight: "clamp" }) : 0;
  const nameTyped = frame >= 150;
  const descTyped = frame >= 210;
  const judgesSet = frame >= 330;
  const created = frame >= 410;

  return (
    <AbsoluteFill>
      <ScreenMock url="raley.koda-software.com/dashboard">
        <DashboardMock />
        {/* Modal overlay */}
        {showModal && (
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", justifyContent: "center", alignItems: "center", opacity: modalOp, zIndex: 50 }}>
            <div style={{ background: "#111113", border: "1px solid #2a2a32", padding: 48, minWidth: 540, maxWidth: 580 }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: 1, marginBottom: 32 }}>New Competition</div>

              {/* Competition Name */}
              <div style={{ marginBottom: 20 }}>
                <div style={fieldLabel}>Competition Name</div>
                <div id="modal-name" style={{ ...inputField, color: nameTyped ? "#e4e4e7" : "#52525b" }}>
                  {nameTyped ? "Boardstock Invitational 2026" : "e.g. Summer Nationals 2026"}
                </div>
              </div>

              {/* Description */}
              <div style={{ marginBottom: 20 }}>
                <div style={fieldLabel}>Description (optional)</div>
                <div id="modal-desc" style={{ ...inputField, color: descTyped ? "#e4e4e7" : "#52525b" }}>
                  {descTyped ? "Annual wakeboard championship at Boardstock" : "Brief description..."}
                </div>
              </div>

              {/* Judge Count — 1-10 number buttons */}
              <div style={{ marginBottom: 24 }}>
                <div style={fieldLabel}>Judge Count</div>
                <div style={{ display: "flex", gap: 4 }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                    <div
                      key={n}
                      id={`modal-j${n}`}
                      style={{
                        width: 36,
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 12,
                        fontFamily: "'JetBrains Mono', monospace",
                        fontWeight: judgesSet && n === 5 ? 700 : 500,
                        background: judgesSet && n === 5 ? "#3b82f6" : (!judgesSet && n === 3 ? "#3b82f6" : "#1a1a1e"),
                        color: (judgesSet && n === 5) || (!judgesSet && n === 3) ? "#fff" : "#52525b",
                        border: `1px solid ${(judgesSet && n === 5) || (!judgesSet && n === 3) ? "#3b82f6" : "#2a2a32"}`,
                      }}
                    >
                      {n}
                    </div>
                  ))}
                </div>
              </div>

              {/* Scoring Model — only Max available */}
              <div style={{ marginBottom: 28 }}>
                <div style={fieldLabel}>
                  Scoring Model
                </div>
                <div
                  id="modal-model-max"
                  style={{
                    background: "#0a0a0b",
                    border: "1px solid #3b82f6",
                    padding: "10px 14px",
                    marginTop: 4,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 13, color: "#e4e4e7" }}>Best Score (Max)</span>
                    <span style={{ fontSize: 11, color: "#71717a", marginLeft: 8 }}>Rider keeps only their highest attempt score</span>
                  </div>
                  <span style={{ fontSize: 10, color: "#3b82f6", fontWeight: 700 }}>SELECTED</span>
                </div>
              </div>

              {/* Action buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}>
                <div style={{ padding: "10px 20px", fontSize: 13, fontWeight: 600, color: "#71717a", textTransform: "uppercase" }}>Cancel</div>
                <div
                  id="modal-create"
                  style={{
                    padding: "10px 24px",
                    background: created ? "#22c55e" : "#3b82f6",
                    color: "#fff",
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    textAlign: "center",
                  }}
                >
                  {created ? "CREATED" : "CREATE COMPETITION"}
                </div>
              </div>
            </div>
          </div>
        )}
        <AutoCursor points={[
          { x: 500, y: 300, frame: 0 },
          { x: 500, y: 300, frame: 20 },
          { target: "new-comp-btn", frame: 40 },
          { target: "new-comp-btn", frame: 65, click: true },
          { target: "modal-name", frame: 110 },
          { target: "modal-name", frame: 150, click: true },
          { target: "modal-desc", frame: 180 },
          { target: "modal-desc", frame: 210, click: true },
          { target: "modal-model-max", frame: 250 },
          { target: "modal-model-max", frame: 300 },
          { target: "modal-j5", frame: 310 },
          { target: "modal-j5", frame: 330, click: true },
          { target: "modal-create", frame: 380 },
          { target: "modal-create", frame: 410, click: true },
          { x: 500, y: 400, frame: 470 },
          { x: 500, y: 400, frame: 530 },
        ]} />
      </ScreenMock>
      <StepLabel text="Click New Competition from the dashboard" show={10} hide={75} color="#a855f7" />
      <StepLabel text="Name your competition" show={90} hide={175} color="#a855f7" />
      <StepLabel text="Add an optional description" show={180} hide={245} color="#a855f7" />
      <StepLabel text="Scoring model is set to Best Score (Max) — highest attempt wins" show={250} hide={310} color="#3b82f6" />
      <StepLabel text="Set your judge count (1-10)" show={310} hide={385} color="#eab308" />
      <StepLabel text="Competition created — ready for setup" show={410} hide={530} color="#22c55e" />
    </AbsoluteFill>
  );
}

function Done() {
  const frame = useCurrentFrame();
  const op = interpolate(frame, [0, 12], [0, 1], { extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={{ justifyContent: "center", alignItems: "center", opacity: op }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: "#22c55e", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}><span style={{ fontSize: 24, color: "#fff" }}>&#10003;</span></div>
      <div style={{ fontSize: 28, fontWeight: 700, color: "#fff" }}>Next: set up heats and invite your team</div>
    </AbsoluteFill>
  );
}

/* ── Dashboard Mock ── */

function DashboardMock() {
  return (
    <div style={shell}>
      <div style={topbar}>
        <span style={brand}>RALEY</span>
        <div style={{ display: "flex", gap: 16 }}>
          {["Competitions", "Riders", "Training", "Support"].map(t => (
            <span key={t} style={{ ...navLink, color: t === "Competitions" ? "#e4e4e7" : "#71717a" }}>{t}</span>
          ))}
        </div>
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#71717a", marginLeft: "auto" }}>admin@koda-software.com</span>
      </div>
      <div style={{ padding: 24 }}>
        {/* Page header — matches Dashboard.tsx */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #2a2a32", paddingBottom: 16, marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>Competitions</div>
            <div style={{ fontSize: 13, color: "#71717a", marginTop: 4 }}>Competitions and events you manage or judge</div>
          </div>
          <div id="new-comp-btn" style={btnPrimary}>NEW COMPETITION</div>
        </div>

        {/* Section label — matches "My Competitions" h2 */}
        <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", color: "#71717a", marginBottom: 12, fontWeight: 600 }}>My Competitions</div>

        {/* Competition cards — 3-col grid matching grid-3 */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
          {([
            { name: "Red Bull Wake Open", model: "Best Score", judges: 5, desc: "Pro invitational" },
            { name: "Summer Nationals", model: "Best Score", judges: 3, desc: null },
            { name: "Moomba Masters", model: "Best Score", judges: 3, desc: "Melbourne event" },
          ]).map(c => (
            <div key={c.name} style={card}>
              <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>{c.name}</div>
              {c.desc && <div style={{ fontSize: 11, color: "#71717a", marginTop: 6 }}>{c.desc}</div>}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <span style={{ ...badgeStyle, color: "#3b82f6" }}>{c.model}</span>
                <span style={{ ...badgeStyle, color: "#71717a" }}>{c.judges} judges</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const shell: React.CSSProperties = { width: "100%", height: "100%", background: "#0a0a0b", fontFamily: "'Inter', sans-serif", color: "#e4e4e7", display: "flex", flexDirection: "column" };
const topbar: React.CSSProperties = { height: 48, background: "#111113", borderBottom: "1px solid #2a2a32", display: "flex", alignItems: "center", gap: 24, padding: "0 24px", flexShrink: 0 };
const brand: React.CSSProperties = { fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: 15, letterSpacing: 2 };
const navLink: React.CSSProperties = { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, color: "#71717a" };
const card: React.CSSProperties = { background: "#111113", border: "1px solid #2a2a32", padding: 20 };
const badgeStyle: React.CSSProperties = { display: "inline-flex", padding: "2px 8px", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, background: "#222228", border: "1px solid #2a2a32", fontFamily: "'JetBrains Mono', monospace" };
const btnPrimary: React.CSSProperties = { padding: "8px 16px", background: "#3b82f6", color: "#fff", fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 };
const fieldLabel: React.CSSProperties = { fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.5, color: "#71717a", marginBottom: 6, fontFamily: "'JetBrains Mono', monospace" };
const inputField: React.CSSProperties = { background: "#0a0a0b", border: "1px solid #2a2a32", padding: "10px 14px", fontSize: 13 };
